import type { SocialSignInInput } from "@atlas/shared";

// Sign in with Apple JS + Google Identity Services (ATL-130). Both SDKs are
// loaded on demand from the vendor CDN (allowed in the CloudFront CSP, see
// infra/web.ts) and both hand back a signed identity token that the API
// verifies server-side; nothing here is trusted on its own. A provider whose
// client id is not configured at build time is simply not offered - same
// pattern as the Turnstile site key.

export const APPLE_SERVICES_ID: string =
  (import.meta.env["VITE_APPLE_SERVICES_ID"] as string | undefined) ?? "";
export const GOOGLE_CLIENT_ID: string =
  (import.meta.env["VITE_GOOGLE_CLIENT_ID"] as string | undefined) ?? "";

const APPLE_SCRIPT =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const GOOGLE_SCRIPT = "https://accounts.google.com/gsi/client";

interface AppleSignInResponse {
  authorization: { code: string; id_token: string; state?: string };
  user?: { email?: string; name?: { firstName?: string; lastName?: string } };
}

interface AppleIdApi {
  auth: {
    init: (opts: {
      clientId: string;
      scope: string;
      redirectURI: string;
      state?: string;
      nonce?: string;
      usePopup: boolean;
    }) => void;
    signIn: () => Promise<AppleSignInResponse>;
  };
}

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

interface GoogleIdApi {
  accounts: {
    id: {
      initialize: (opts: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        ux_mode?: "popup" | "redirect";
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        itp_support?: boolean;
      }) => void;
      renderButton: (
        el: HTMLElement,
        opts: {
          type?: "standard" | "icon";
          theme?: "outline" | "filled_blue" | "filled_black";
          size?: "large" | "medium" | "small";
          text?: "signin_with" | "signup_with" | "continue_with" | "signin";
          shape?: "rectangular" | "pill" | "circle" | "square";
          logo_alignment?: "left" | "center";
          width?: number;
          locale?: string;
        },
      ) => void;
    };
  };
}

declare global {
  interface Window {
    AppleID?: AppleIdApi;
    google?: GoogleIdApi;
  }
}

const scriptPromises = new Map<string, Promise<void>>();

function loadScript(src: string, ready: () => boolean): Promise<void> {
  if (ready()) return Promise.resolve();
  let p = scriptPromises.get(src);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const el = document.createElement("script");
      el.src = src;
      el.async = true;
      el.defer = true;
      el.onload = () => resolve();
      el.onerror = () => {
        scriptPromises.delete(src);
        reject(new Error(`Failed to load ${src}`));
      };
      document.head.appendChild(el);
    });
    scriptPromises.set(src, p);
  }
  return p;
}

function randomToken(bytes = 16): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

export class SocialSignInCancelled extends Error {
  constructor() {
    super("Sign-in was cancelled");
  }
}

// ─── Apple ────────────────────────────────────────────────────────────────────

let appleInitialisedFor: string | null = null;

/**
 * Opens Apple's popup and resolves with the identity token, the one-time
 * authorization code and (first sign-in only) the user's name. The popup
 * must be opened from a click handler or browsers block it.
 */
export async function signInWithApple(): Promise<Pick<SocialSignInInput, "idToken" | "authorizationCode" | "nonce" | "name">> {
  if (!APPLE_SERVICES_ID) throw new Error("Sign in with Apple is not configured");
  await loadScript(APPLE_SCRIPT, () => !!window.AppleID);
  const api = window.AppleID;
  if (!api) throw new Error("Sign in with Apple failed to load");

  const nonce = randomToken();
  // The return URL must be registered on the services id in the Apple
  // developer portal; the API also passes it back on the code exchange.
  const redirectURI = `${window.location.origin}/login`;
  const key = `${APPLE_SERVICES_ID}|${nonce}`;
  if (appleInitialisedFor !== key) {
    api.auth.init({
      clientId: APPLE_SERVICES_ID,
      scope: "name email",
      redirectURI,
      nonce,
      usePopup: true,
    });
    appleInitialisedFor = key;
  }

  let res: AppleSignInResponse;
  try {
    res = await api.auth.signIn();
  } catch (err) {
    const code = (err as { error?: string } | null)?.error;
    if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
      throw new SocialSignInCancelled();
    }
    throw new Error("Sign in with Apple did not complete. Try again.");
  }

  const first = res.user?.name?.firstName?.trim() ?? "";
  const last = res.user?.name?.lastName?.trim() ?? "";
  const name = `${first} ${last}`.trim();
  return {
    idToken: res.authorization.id_token,
    authorizationCode: res.authorization.code,
    nonce,
    ...(name ? { name } : {}),
  };
}

// ─── Google ───────────────────────────────────────────────────────────────────

/**
 * Renders Google's official button into `el` and calls `onCredential` with
 * the identity token when the user picks an account. Google Identity
 * Services only hands out tokens through its own rendered button or One
 * Tap, so there is no imperative "open the popup" equivalent to Apple's.
 */
export async function renderGoogleButton(
  el: HTMLElement,
  onCredential: (idToken: string) => void,
  opts: { width?: number; text?: "signin_with" | "signup_with" | "continue_with" } = {},
): Promise<void> {
  if (!GOOGLE_CLIENT_ID) return;
  await loadScript(GOOGLE_SCRIPT, () => !!window.google?.accounts?.id);
  const api = window.google?.accounts.id;
  if (!api) throw new Error("Google sign-in failed to load");
  api.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response) => {
      if (response.credential) onCredential(response.credential);
    },
    ux_mode: "popup",
    auto_select: false,
    cancel_on_tap_outside: true,
    itp_support: true,
  });
  el.replaceChildren();
  api.renderButton(el, {
    type: "standard",
    theme: "filled_black",
    size: "large",
    text: opts.text ?? "continue_with",
    shape: "rectangular",
    logo_alignment: "left",
    ...(opts.width ? { width: Math.min(400, Math.round(opts.width)) } : {}),
  });
}
